// import Geolocation from '@react-native-community/geolocation'
import moment from 'moment'
import { IReservation, IServiceInfo, IStop } from '../models'
import { LiteralsService } from '../services'
import { IWeekday } from '../models/weekday'
import countries from '../countries.json'

const lowercaseRegExp = /[a-z]/
const uppercaseRegExp = /[A-Z]/
const digitsRegExp = /[0-9]/
const specialCharactersRegExp = /[\^$\*.\[\]{}()?\-"!@#%&\/\\,><':;|_~`]/
const otherCharsRegExp = /[=+]/
const lengthRegExp = /^.{8,}$/
const validDniNifLetters = 'TRWAGMYFPDXBNJZSQVHLCKET'
const nifRegExp = new RegExp(/^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/)
const dniRegExp = new RegExp(/^[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/)

// Short labels per language for the weekday picker; the English long names are the
// wire format the backend expects in daysOfWeek.
export const weekDaysByLanguage: Record<string, { short: string[], long: string[] }> = {
  es: { short: ['Lu','Ma','Mi','Ju','Vi','Sá','Do'], long: ['LUNES','MARTES','MIÉRCOLES','JUEVES','VIERNES','SÁBADO','DOMINGO'] },
  ca: { short: ['Dl','Dm','Dc','Dj','Dv','Ds','Dg'], long: ['DILLUNS','DIMARTS','DIMECRES','DIJOUS','DIVENDRES','DISSABTE','DIUMENGE'] },
  en: { short: ['Mo','Tu','We','Th','Fr','Sa','Su'], long: ['MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY','SUNDAY'] },
  it: { short: ['Lu','Ma','Me','Gi','Ve','Sa','Do'], long: ['LUNEDÌ','MARTEDÌ','MERCOLEDÌ','GIOVEDÌ','VENERDÌ','SABATO','DOMENICA'] },
  pt: { short: ['Se','Te','Qu','Qi','Sx','Sá','Do'], long: ['SEGUNDA','TERÇA','QUARTA','QUINTA','SEXTA','SÁBADO','DOMINGO'] },
  eu: { short: ['Al','As','Az','Og','Or','Lr','Ig'], long: ['ASTELEHENA','ASTEARTEA','ASTEAZKENA','OSTEGUNA','OSTIRALA','LARUNBATA','IGANDEA'] },
  el: { short: ['Δε','Τρ','Τε','Πε','Πα','Σα','Κυ'], long: ['ΔΕΥΤΈΡΑ','ΤΡΊΤΗ','ΤΕΤΆΡΤΗ','ΠΈΜΠΤΗ','ΠΑΡΑΣΚΕΥΉ','ΣΆΒΒΑΤΟ','ΚΥΡΙΑΚΉ'] },
  fr: { short: ['Lu','Ma','Me','Je','Ve','Sa','Di'], long: ['LUNDI','MARDI','MERCREDI','JEUDI','VENDREDI','SAMEDI','DIMANCHE'] },
  hr: { short: ['Po','Ut','Sr','Če','Pe','Su','Ne'], long: ['PONEDJELJAK','UTORAK','SRIJEDA','ČETVRTAK','PETAK','SUBOTA','NEDJELJA'] },
  hu: { short: ['Hé','Ke','Sz','Cs','Pé','Szo','Va'], long: ['HÉTFŐ','KEDD','SZERDA','CSÜTÖRTÖK','PÉNTEK','SZOMBAT','VASÁRNAP'] },
  si: { short: ['Po','To','Sr','Če','Pe','So','Ne'], long: ['PONEDELJEK','TOREK','SREDA','ČETRTEK','PETEK','SOBOTA','NEDELJA'] },
  de: { short: ['Mo','Di','Mi','Do','Fr','Sa','So'], long: ['MONTAG','DIENSTAG','MITTWOCH','DONNERSTAG','FREITAG','SAMSTAG','SONNTAG'] },
  nl: { short: ['Ma','Di','Wo','Do','Vr','Za','Zo'], long: ['MAANDAG','DINSDAG','WOENSDAG','DONDERDAG','VRIJDAG','ZATERDAG','ZONDAG'] }
}

export function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * A theme colour at a given opacity, written as a percentage:
 * `withOpacity(theme.colors.white, 80)` -> `rgba(255, 255, 255, 0.8)`.
 *
 * Reads better than the hex alpha suffixes scattered around the styles (`${colors.white}cc`),
 * where the actual percentage is anyone's guess. Non hex colours are returned untouched.
 */
export function withOpacity(color: string, percentage: number): string {
  const hex = color.replace('#', '')
  const full = hex.length === 3 ? hex.split('').map(char => char + char).join('') : hex

  if (!/^[0-9a-f]{6}$/i.test(full)) return color

  const [red, green, blue] = [0, 2, 4].map(index => parseInt(full.slice(index, index + 2), 16))
  const alpha = Math.min(Math.max(percentage, 0), 100) / 100

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

export function dateAsCalendar(date: Date | string, withTime = true) {
  const dateFormatted = moment(date)
  const today = moment()
  const yesterday = moment().subtract(1, 'day')
  const tomorrow = moment().add(1, 'day')
  let stringFormat = withTime ? '[-] HH:mm' : ''

  if (moment(date).isSame(today, 'day')) {
    stringFormat = `[${LiteralsService.get('today', true)}]`
  } else if (moment(date).isSame(yesterday, 'day')) {
    stringFormat = `[${LiteralsService.get('yesterday', true)}]`
  } else if (moment(date).isSame(tomorrow, 'day')) {
    stringFormat = `[${LiteralsService.get('tomorrow', true)}]`
  } else {
    stringFormat = 'DD/MM/YYYY'
  }

  return dateFormatted.format(stringFormat + (withTime ? ' [-] HH:mm' : ''))
}

export function penaltyAsCalendar(date: Date | string): string {
  const dateFormatted = moment(date)
  const today = moment()
  const tomorrow = moment().add(1, 'day')
  let response = ''

  if (dateFormatted.diff(today, 'days') < 1) {
    response = LiteralsService.get('expired', true)
  } else if (moment(date).isSame(tomorrow, 'day')) {
    response = LiteralsService.get('tomorrow', true)
  } else {
    response = `${LiteralsService.get('expiresIn', true)} ${dateFormatted.diff(
      today,
      'days'
    )} ${LiteralsService.get('days', true)}`
  }

  return response
}

export function hasTelephonePrefix(telephone: string): boolean {
  const regExp = new RegExp('^\\+[0-9]{2,2}')
  return regExp.test(telephone)
}

// Countries whose mobiles never start with their own calling code digits (e.g. German
// mobiles always start with "1", never "49"; Croatian mobiles always start with "9", never
// "385"). For these, a leading bare match of the calling code is unambiguously a duplicated
// prefix — safe to strip regardless of length.
const UNAMBIGUOUS_BARE_PREFIX_CODES = new Set(['49', '385']) // Germany, Croatia

// Fixed length (digits) of a mobile number, excluding the country code — mobile only.
// Used to arbitrate countries where mobiles CAN legitimately start with the same digits as
// the calling code (e.g. Italian mobiles in the 393xxxxxxx range): if what's typed already
// has the expected number of digits, it's treated as a real number, not a duplicated prefix.
const MOBILE_LOCAL_LENGTHS: Record<string, number> = {
  '376': 6,  // Andorra
  '32': 9,   // Belgium
  '33': 9,   // France
  '30': 10,  // Greece
  '36': 9,   // Hungary
  '39': 10,  // Italy
  '965': 8,  // Kuwait
  '351': 9,  // Portugal
  '386': 8,  // Slovenia
  '34': 9,   // Spain
  '44': 10,  // United Kingdom
}

// The E.164 number Cognito knows the account by, from what the user typed and the picked country.
// Mirrors the app: a prefix typed by hand ("+351…", "00351…", "351…") isn't added twice.
export function sanitizeTelephone(telephone: string, callingCode?: string): string {
  telephone = (telephone || '').replace(/[\s\-().]/g, '')
  if (!callingCode) return telephone

  const codeDigits = callingCode.replace(/\D/g, '')
  if (!codeDigits) return `${callingCode}${telephone}`

  // "+351..." or "00351..." are unambiguous: no local number legitimately starts that way
  const explicitPrefix = new RegExp(`^(?:\\+|00)${codeDigits}`)
  if (explicitPrefix.test(telephone)) {
    return `${callingCode}${telephone.replace(explicitPrefix, '')}`
  }
  // A full international number with another country's code ("+34…" while Portugal is picked)
  // already says which country it is.
  if (telephone.startsWith('+')) return telephone

  const barePrefix = new RegExp(`^${codeDigits}`)
  if (!barePrefix.test(telephone)) return `${callingCode}${telephone}`

  if (UNAMBIGUOUS_BARE_PREFIX_CODES.has(codeDigits)) {
    return `${callingCode}${telephone.replace(barePrefix, '')}`
  }

  // Otherwise (too long/short vs. the expected mobile length), assume the prefix got
  // duplicated and strip it.
  const expectedLength = MOBILE_LOCAL_LENGTHS[codeDigits]
  if (expectedLength && telephone.length !== expectedLength) {
    return `${callingCode}${telephone.replace(barePrefix, '')}`
  }

  return `${callingCode}${telephone}`
}

export function isEmailValid(email: string): boolean {
  const regExp = new RegExp('[a-zA-Z0-9\-]+@[\-a-zA-Z]+[.][a-zA-Z]{2,4}')
  return regExp.test(email)
}

export function isCodiceValid(codice: string): boolean {
  // eslint-disable-next-line max-len
  const regExp = new RegExp('^([A-Za-z]{6}[0-9lmnpqrstuvLMNPQRSTUV]{2}[abcdehlmprstABCDEHLMPRST]{1}[0-9lmnpqrstuvLMNPQRSTUV]{2}[A-Za-z]{1}[0-9lmnpqrstuvLMNPQRSTUV]{3}[A-Za-z]{1})|([0-9]{11})$')
  return regExp.test(codice)
}

export function isPasswordValid(password: string): boolean {
  let isValid = true

  if (!lowercaseRegExp.test(password)) isValid = false
  // if (!uppercaseRegExp.test(password)) isValid = false
  if (!digitsRegExp.test(password)) isValid = false
  // if (!specialCharactersRegExp.test(password)) isValid = false
  if (!lengthRegExp.test(password)) isValid = false
  if (!Array.from(password).every(isCharValid)) isValid = false

  return isValid
}

function isCharValid(character: string): boolean {
  return (
    lowercaseRegExp.test(character) ||
    uppercaseRegExp.test(character) ||
    digitsRegExp.test(character) ||
    specialCharactersRegExp.test(character) ||
    otherCharsRegExp.test(character)
  )
}

export function isTextValid(text: string): boolean {
  const regExp = new RegExp(
    '^[a-zA-Zα-ωΑ-ΩίϊΐόάέύϋΰήώáàäéèëíìïóòöúùüÁÀÄÉÈËIÍÌÏÓÒÖÚÙÜñçÑÇ ]{2,}$'
  )
  return regExp.test(text)
}

export function isTelephoneValid(telephone: string): boolean {
  const regExp = new RegExp('^(\\+?[0-9]{1,3})?[0-9]{9}$')
  return regExp.test(telephone)
}

export function isDniValid(dni: string): boolean {
  if (!nifRegExp.test(dni) && !dniRegExp.test(dni)) return false

  const nie = dni
    .replace(/^[X]/, '0')
    .replace(/^[Y]/, '1')
    .replace(/^[Z]/, '2')
  const letter = dni.substr(-1)
  const letterIndex = parseInt(nie.substr(0, 8), 10) % 23

  return validDniNifLetters.charAt(letterIndex) === letter
}

export function isPortugueseNifValid(nif: string): boolean {
  if (!/^[0-9]{9}$/.test(nif)) return false

  const digits = nif.split('').map(Number)
  const sum = digits.slice(0, 8).reduce((total, digit, index) => total + digit * (9 - index), 0)
  const remainder = sum % 11
  const checkDigit = remainder < 2 ? 0 : 11 - remainder

  return digits[8] === checkDigit
}

export function isVerificationCodeValid(code: string): boolean {
  const regExp = new RegExp('^[0-9]{6}$')
  return regExp.test(code)
}

export function compareStops(first: IStop, second: IStop): boolean {
  return first.id === second.id && first.name === second.name
}

/**
 * A single stop service is booked from one stop alone: picking it does not narrow the stops left
 * to pick (there are no destinations to fetch), so the rest of stops stay available.
 */
export function isSingleStopPick(origin?: IStop | null, destination?: IStop | null): boolean {
  const picked = origin && !destination ? origin : !origin && destination ? destination : undefined
  return !!picked?.serviceInfo?.singleStopReservations
}

/**
 * With merged stops the API can hand back the same physical stop twice: the merged stop (negative
 * id), the one to board at, and the line's final stop (positive id), only good as a destination —
 * same name, same coordinates. The screen shows it once: before an origin is picked, the merged
 * one; once there is an origin, whichever of the two the destinations allow. A picked stop always
 * stays, so its marker and row keep their highlight. Twins are looked for within one service:
 * results that mix services carry their serviceInfo and are grouped by it.
 */
export function withoutTwinStops<T extends IStop>(
  stops: T[],
  pickedOrigin?: IStop | null,
  pickedDestination?: IStop | null,
  destinationStops: IStop[] = []
): T[] {
  const keyOf = (stop: IStop): string => {
    const coordinates = stop.location?.geometry?.coordinates ?? []
    return `${ stop.serviceInfo?.id ?? '' }|${ stop.name }|${ coordinates.join(',') }`
  }

  const twins = new Map<string, T[]>()
  stops.forEach(stop => {
    const key = keyOf(stop)
    twins.set(key, [...(twins.get(key) ?? []), stop])
  })

  const shown = new Set<T>()
  twins.forEach(group => {
    if (group.length < 2) return shown.add(group[0])

    const isPicked = (stop: IStop) => stop.id === pickedOrigin?.id || stop.id === pickedDestination?.id
    const isDestination = (stop: IStop) => destinationStops.some(destination => destination.id === stop.id)
    const representative =
      group.find(isPicked) ??
      (pickedOrigin && group.find(isDestination)) ??
      group.find(stop => stop.id < 0) ??
      group[0]
    shown.add(representative)
  })

  return stops.filter(stop => shown.has(stop))
}

export function repeatStringForService(
  stringFormat: string,
  destinationStopId: number,
  totalRepeat: number
): string {
  let resultString = stringFormat
  for (let index = 0; index < totalRepeat; index++) {
    resultString += `${destinationStopId},`
  }

  return resultString.slice(0, resultString.length - 1)
}

export function addDelayToReservation(
  reservation: IReservation,
  delay: number
): IReservation {
  return {
    ...reservation,
    beginningStop: {
      ...reservation.beginningStop,
      dateTime: moment(reservation?.beginningStop.dateTime ?? false)
        .add(delay, 'minutes')
        .toISOString()
    },
    seats: reservation.seats.map(s => ({
      ...s,
      exitStop: {
        ...s.exitStop,
        dateTime: moment(s.exitStop.dateTime)
          .add(delay, 'minutes')
          .toISOString()
      }
    }))
  }
}

export function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function isWheelPicker(serviceInfos: IServiceInfo[]): boolean {
  return serviceInfos.some((serviceInfo: IServiceInfo) => serviceInfo.bookingTimePicker) ? true : false
}

export function getWeekDays(daysList?: string[]): IWeekday[] {
  const listResult: IWeekday[] = []
  const currentDate = moment().toDate()

  for (let i = 0; i < (daysList !== undefined ? daysList.length : 7); i++) {
      let date = new Date()
      if (daysList !== undefined) {
        date = new Date(daysList[i])
      } else {
        date.setDate(currentDate.getDate() + i)
      }
      const day = date.getDate().toString()
      let weekday = getdayOfTheWeek(date.getDay())
      
      const today = moment()
      const tomorrow = moment().add(1, 'days')
      if (moment(date).isSame(today, 'days')) weekday = LiteralsService.get('today', true)
      else if (moment(date).isSame(tomorrow, 'days')) weekday = LiteralsService.get('tomorrow', true)

      const month = getMonth(date.getMonth())

      const result: IWeekday = {day: day, weekday: weekday, month: month, date: date, index: i}
      listResult.push(result)
  }

  return listResult
}

export function getdayOfTheWeek(weekday: number): String {
  const weekdays = [
    LiteralsService.get('sunday', true),
    LiteralsService.get('monday', true),
    LiteralsService.get('tuesday', true),
    LiteralsService.get('wednesday', true),
    LiteralsService.get('thursday', true),
    LiteralsService.get('friday', true),
    LiteralsService.get('saturday', true)
  ]
  return weekdays[weekday]
}

export function getMonth(month: number): string {
  const months = [    
    LiteralsService.get('jan', true),
    LiteralsService.get('feb', true),
    LiteralsService.get('mar', true),
    LiteralsService.get('apr', true),
    LiteralsService.get('may', true),
    LiteralsService.get('jun', true),
    LiteralsService.get('jul', true),
    LiteralsService.get('aug', true),
    LiteralsService.get('sept', true),
    LiteralsService.get('oct', true),
    LiteralsService.get('nov', true),
    LiteralsService.get('dec', true)
]
  return months[month]
}

export function calculateMinutesToArrive(expectedDateArrival: string): number {
  const currentDate = moment()
  const targetDate = moment(expectedDateArrival)
  const timeDifference = targetDate.diff(currentDate, 'minutes')
  return Math.floor(timeDifference)
}

export function firstLetterLowercase(string: string): string {
  return string.charAt(0).toLowerCase() + string.slice(1)
}

export function handleBottomScroll(callback: any) {
  const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
  const bottom = (clientHeight + scrollTop) >= (scrollHeight - 300)
  if (bottom) callback()
}

export function getCountryByCode(code: string) {
  return countries.find(country => {
    return country.code === code
  })
}